/**
 * Cleanup Modal
 * Provides options to remove files from playlist based on various criteria
 */

import { playlistState, sortingState } from './state.js';
import { updatePlaylistUI } from './playlist.js';
import { isPlaybackBlocked } from './playback.js';

class CleanupModal {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.options = {
      removeErrors: false,
      removeLessThan: false,
      removeMoreThan: false,
      removeWildcard: false
    };
    this.lessThanDuration = 5; // default 5 seconds
    this.moreThanDuration = 300; // default 5 minutes
    this.wildcardPattern = '';
  }

  /**
   * Initialize the cleanup modal
   */
  init() {
    this.createModal();
    this.attachEventListeners();
  }

  /**
   * Create the cleanup modal HTML
   */
  createModal() {
    const modalHTML = `
      <div id="cleanupModal" class="cleanup-modal" style="display: none;">
        <div class="cleanup-modal-backdrop"></div>
        <div class="cleanup-modal-content">
          <button class="cleanup-modal-close">&times;</button>

          <h2 class="cleanup-title">Clean Up Playlist</h2>
          <p class="cleanup-description">Select which files to remove from the playlist:</p>

          <div class="cleanup-options">
            <!-- Remove files with errors -->
            <label class="cleanup-option cleanup-option-error">
              <input type="checkbox" id="cleanupRemoveErrors">
              <div class="cleanup-option-content">
                <span class="cleanup-option-label">Files with errors</span>
                <span class="cleanup-option-hint" id="cleanupErrorCount">0 files cannot be loaded</span>
              </div>
            </label>

            <!-- Remove files less than duration -->
            <label class="cleanup-option">
              <input type="checkbox" id="cleanupRemoveLessThan">
              <div class="cleanup-option-content">
                <span class="cleanup-option-label">Files shorter than</span>
                <span class="cleanup-option-hint">Remove short clips or samples</span>
                <div class="cleanup-duration-inputs">
                  <input type="number" id="cleanupLessThanValue" class="cleanup-duration-input" value="5" min="1" max="3600" onclick="event.stopPropagation()">
                  <span class="cleanup-duration-label">seconds</span>
                </div>
              </div>
            </label>

            <!-- Remove files more than duration -->
            <label class="cleanup-option">
              <input type="checkbox" id="cleanupRemoveMoreThan">
              <div class="cleanup-option-content">
                <span class="cleanup-option-label">Files longer than</span>
                <span class="cleanup-option-hint">Remove overly long files</span>
                <div class="cleanup-duration-inputs">
                  <input type="number" id="cleanupMoreThanValue" class="cleanup-duration-input" value="300" min="1" max="3600" onclick="event.stopPropagation()">
                  <span class="cleanup-duration-label">seconds</span>
                </div>
              </div>
            </label>

            <!-- Remove files matching wildcard -->
            <label class="cleanup-option">
              <input type="checkbox" id="cleanupRemoveWildcard">
              <div class="cleanup-option-content">
                <span class="cleanup-option-label">Files matching pattern</span>
                <span class="cleanup-option-hint">Use * as wildcard (e.g., *demo*, test_*)</span>
                <input type="text" id="cleanupWildcardValue" class="cleanup-wildcard-input" placeholder="*pattern*" onclick="event.stopPropagation()">
              </div>
            </label>
          </div>

          <div class="cleanup-summary" id="cleanupSummary">
            <span class="cleanup-summary-text">
              <span class="cleanup-summary-count" id="cleanupAffectedCount">0</span> files will be removed
            </span>
          </div>

          <div class="cleanup-actions">
            <button class="btn-cancel" id="cleanupCancelBtn">Cancel</button>
            <button class="btn-remove" id="cleanupRemoveBtn" disabled>Remove Files</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('cleanupModal');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close modal
    this.modal.querySelector('.cleanup-modal-backdrop').addEventListener('click', () => this.hide());
    this.modal.querySelector('.cleanup-modal-close').addEventListener('click', () => this.hide());
    this.modal.querySelector('#cleanupCancelBtn').addEventListener('click', () => this.hide());

    // Option checkboxes
    this.modal.querySelector('#cleanupRemoveErrors').addEventListener('change', (e) => {
      this.options.removeErrors = e.target.checked;
      this.updateSummary();
    });

    this.modal.querySelector('#cleanupRemoveLessThan').addEventListener('change', (e) => {
      this.options.removeLessThan = e.target.checked;
      this.updateSummary();
    });

    this.modal.querySelector('#cleanupRemoveMoreThan').addEventListener('change', (e) => {
      this.options.removeMoreThan = e.target.checked;
      this.updateSummary();
    });

    this.modal.querySelector('#cleanupRemoveWildcard').addEventListener('change', (e) => {
      this.options.removeWildcard = e.target.checked;
      this.updateSummary();
    });

    // Duration inputs
    this.modal.querySelector('#cleanupLessThanValue').addEventListener('input', (e) => {
      this.lessThanDuration = parseInt(e.target.value) || 5;
      if (this.options.removeLessThan) {
        this.updateSummary();
      }
    });

    this.modal.querySelector('#cleanupMoreThanValue').addEventListener('input', (e) => {
      this.moreThanDuration = parseInt(e.target.value) || 300;
      if (this.options.removeMoreThan) {
        this.updateSummary();
      }
    });

    // Wildcard input
    this.modal.querySelector('#cleanupWildcardValue').addEventListener('input', (e) => {
      this.wildcardPattern = e.target.value;
      if (this.options.removeWildcard) {
        this.updateSummary();
      }
    });

    // Remove button
    this.modal.querySelector('#cleanupRemoveBtn').addEventListener('click', () => this.performCleanup());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });
  }

  /**
   * Convert wildcard pattern to regex
   */
  wildcardToRegex(pattern) {
    if (!pattern) return null;
    // Escape regex special chars except * and ?
    const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&');
    // Convert wildcards to regex
    const regexStr = escaped.replace(/\*/g, '.*').replace(/\?/g, '.');
    try {
      return new RegExp(`^${regexStr}$`, 'i');
    } catch {
      return null;
    }
  }

  /**
   * Get list of tracks that would be removed
   */
  getAffectedTracks() {
    const affected = new Set();

    for (let i = 0; i < playlistState.playlist.length; i++) {
      const track = playlistState.playlist[i];
      if (!track.included) continue; // Skip already excluded tracks

      // Check errors
      if (this.options.removeErrors && track.error) {
        affected.add(i);
        continue;
      }

      // Check less than duration
      if (this.options.removeLessThan && track.duration !== null && track.duration < this.lessThanDuration) {
        affected.add(i);
        continue;
      }

      // Check more than duration
      if (this.options.removeMoreThan && track.duration !== null && track.duration > this.moreThanDuration) {
        affected.add(i);
        continue;
      }

      // Check wildcard pattern
      if (this.options.removeWildcard && this.wildcardPattern) {
        const regex = this.wildcardToRegex(this.wildcardPattern);
        if (regex && regex.test(track.name)) {
          affected.add(i);
        }
      }
    }

    return Array.from(affected);
  }

  /**
   * Update the summary showing how many files will be affected
   */
  updateSummary() {
    const affected = this.getAffectedTracks();
    const countEl = this.modal.querySelector('#cleanupAffectedCount');
    const removeBtn = this.modal.querySelector('#cleanupRemoveBtn');
    const summaryText = this.modal.querySelector('.cleanup-summary-text');

    // Update count - preserve the id in the innerHTML
    if (affected.length > 0) {
      removeBtn.disabled = false;
      summaryText.innerHTML = `<span class="cleanup-summary-count cleanup-summary-warning" id="cleanupAffectedCount">${affected.length}</span> file${affected.length !== 1 ? 's' : ''} will be removed`;
    } else {
      removeBtn.disabled = true;
      summaryText.innerHTML = `<span class="cleanup-summary-count" id="cleanupAffectedCount">0</span> files will be removed`;
    }
  }

  /**
   * Update error count display
   */
  updateErrorCount() {
    const errorCount = playlistState.playlist.filter(t => t.included && t.error).length;
    const countEl = this.modal.querySelector('#cleanupErrorCount');
    countEl.textContent = `${errorCount} file${errorCount !== 1 ? 's' : ''} cannot be loaded`;
  }

  /**
   * Perform the cleanup operation
   */
  performCleanup() {
    // Block playlist modifications during export
    if (isPlaybackBlocked()) return;

    const affected = this.getAffectedTracks();

    if (affected.length === 0) {
      this.hide();
      return;
    }

    // Save reference to current track before splicing
    const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];

    // Collect removed tracks for customOrder filtering
    const removedTracks = affected.map(i => playlistState.playlist[i]);

    // Remove tracks in reverse order to maintain indices
    affected.sort((a, b) => b - a);

    for (const index of affected) {
      playlistState.playlist.splice(index, 1);
    }

    // Update current track index using reference identity (matches deleteExcludedTracks pattern)
    if (currentTrack) {
      const newIndex = playlistState.playlist.indexOf(currentTrack);
      if (newIndex >= 0) {
        playlistState.currentTrackIndex = newIndex;
      } else {
        // Current track was removed — fall back to first included or 0
        const firstIncluded = playlistState.playlist.findIndex(t => t.included);
        playlistState.currentTrackIndex = firstIncluded >= 0 ? firstIncluded : 0;
      }
    } else {
      // Playlist empty or stale index — reset to 0
      playlistState.currentTrackIndex = 0;
    }

    // Update custom order to remove deleted tracks
    if (sortingState.mode === 'custom') {
      sortingState.customOrder = sortingState.customOrder.filter(t => !removedTracks.includes(t));
    }

    // Clear selection
    playlistState.selectedIndices = [];

    // Update UI
    updatePlaylistUI();
    document.dispatchEvent(new CustomEvent('playlistUpdated'));

    this.hide();

    console.log(`[CleanupModal] Removed ${affected.length} files from playlist`);
  }

  /**
   * Show the cleanup modal
   */
  show() {
    // Reset options
    this.options = {
      removeErrors: false,
      removeLessThan: false,
      removeMoreThan: false,
      removeWildcard: false
    };

    // Reset checkboxes
    this.modal.querySelector('#cleanupRemoveErrors').checked = false;
    this.modal.querySelector('#cleanupRemoveLessThan').checked = false;
    this.modal.querySelector('#cleanupRemoveMoreThan').checked = false;
    this.modal.querySelector('#cleanupRemoveWildcard').checked = false;

    // Update counts
    this.updateErrorCount();
    this.updateSummary();

    this.modal.style.display = 'flex';
    this.isOpen = true;
  }

  /**
   * Hide the cleanup modal
   */
  hide() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }
}

// Export singleton instance
export const cleanupModal = new CleanupModal();
